import { useMemo } from 'react';

import {
  useGetProductsQuery,
  useGetProductSuppliersQuery,
} from '@app/api/Products';
import type { ModelProduct } from '@app/api/Products';
import { useUserPrint } from '@app/hooks';
import { useGetCurrentBranchWarehousesQuery } from '@app/services/rtkQuery/Warehouses/api';
import type { ErrorPair, Options } from '@app/types';

export interface WarehouseOperationData {
  ActiveBranchId: number;
  products: ModelProduct[] | undefined;
  warehousesOptions: Options;
  suppliersOptions: Options;
  isPageLoading: boolean;
  errors: ErrorPair[];
}

export const useWarehouseOperationData = (): WarehouseOperationData => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isUninitialized: isUninitializedWarehouses,
    isFetching: isFetchingWarehouses,
    isError: isErrorWarehouses,
    error: errorWarehouses,
  } = useGetCurrentBranchWarehousesQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  const {
    data: products,
    isLoading: isLoadingProducts,
    isUninitialized: isUninitializedProducts,
    isFetching: isFetchingProducts,
    isError: isErrorProducts,
    error: errorProducts,
  } = useGetProductsQuery({ ActiveBranchId }, { skip: !ActiveBranchId });

  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    isUninitialized: isUninitializedSuppliers,
    isFetching: isFetchingSuppliers,
    isError: isErrorSuppliers,
    error: errorSuppliers,
  } = useGetProductSuppliersQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  const isPageLoading = useMemo(
    () =>
      isLoadingWarehouses ||
      isUninitializedWarehouses ||
      isFetchingWarehouses ||
      isLoadingSuppliers ||
      isUninitializedSuppliers ||
      isFetchingSuppliers ||
      isLoadingProducts ||
      isUninitializedProducts ||
      isFetchingProducts,
    [
      isLoadingWarehouses,
      isUninitializedWarehouses,
      isFetchingWarehouses,
      isLoadingSuppliers,
      isUninitializedSuppliers,
      isFetchingSuppliers,
      isLoadingProducts,
      isUninitializedProducts,
      isFetchingProducts,
    ]
  );

  const warehousesOptions: Options = useMemo(
    () => warehouses?.map(({ Name, Id }) => ({ label: Name, value: Id })) || [],
    [warehouses]
  );

  const suppliersOptions: Options = useMemo(
    () => suppliers?.map(({ Name, Id }) => ({ label: Name, value: Id })) || [],
    [suppliers]
  );

  const errors: ErrorPair[] = useMemo(
    () => [
      { flag: isErrorWarehouses, error: errorWarehouses },
      { flag: isErrorSuppliers, error: errorSuppliers },
      { flag: isErrorProducts, error: errorProducts },
    ],
    [
      isErrorWarehouses,
      errorWarehouses,
      isErrorSuppliers,
      errorSuppliers,
      isErrorProducts,
      errorProducts,
    ]
  );

  return {
    ActiveBranchId,
    products,
    warehousesOptions,
    suppliersOptions,
    isPageLoading,
    errors,
  };
};
